/**
 * Travel ball family signup API, the shared travel ball error vocabulary, and the coach's team list.
 * Mirrors the shape of `services/signup.ts` for the college side: refusals are
 * thrown as a `TravelBallSignupError` whose `code` comes from the response body, so pages branch
 * on the server's own vocabulary rather than reading messages.
 */

import { ApiError, get, post } from './api';
import type {
  CreateTravelBallFamilySignupRequest,
  TravelBallFamilySignupResponse,
} from '../types/travelBallFamily';
import type { TravelBallSignupErrorCode } from '../types/travelBallSignup';

export const RATE_LIMITED_MESSAGE = 'Too many attempts, try again later.';
export const NETWORK_MESSAGE =
  'We could not reach RosterPilot. Check your connection and try again.';
export const UNKNOWN_MESSAGE = 'Something went wrong on our end. Please try again.';

const KNOWN_CODES: ReadonlySet<string> = new Set<TravelBallSignupErrorCode>([
  'invalid_name',
  'invalid_sport',
  'invalid_team_name',
  'invalid_email',
  'email_not_verified',
  'legal_not_accepted',
  'travel_ball_disabled',
  'invalid_family_role',
  'family_role_conflict',
  'query_too_short',
  'team_not_found',
  'already_on_team',
  'already_requested',
  'join_request_cooldown',
  'too_many_pending_requests',
  'player_link_required',
  'already_decided',
  'join_request_not_found',
  // Account linking (Phase 4) and requests made for a linked player (Phase 5).
  'invalid_relationship',
  'relationship_required',
  'link_request_expired',
  'link_not_found',
  'player_cannot_unlink',
  'decline_instead',
  'player_required',
  'player_not_found',
  'cannot_link_self',
  // A player tried to change what only the parent/guardian sets (the relationship).
  'guardian_only',
  // A join request with no team picked.
  'team_required',
  // A family view on a coach route.
  'travel_ball_family_view',
  // The signed-in family account is gone (home, links).
  'not_found',
]);

/** A refused travel ball signup call. `message` is always displayable. */
export class TravelBallSignupError extends Error {
  readonly code: TravelBallSignupErrorCode | 'rate_limited' | 'unauthorized' | 'network' | 'unknown';
  readonly status: number;
  /**
   * Set on a link request's 409 `already_requested`: the waiting request this account already sent
   * to that email, so the page can offer to cancel it.
   */
  readonly linkId: string | null;

  constructor(
    code: TravelBallSignupError['code'],
    message: string,
    status: number,
    linkId: string | null = null,
  ) {
    super(message);
    this.name = 'TravelBallSignupError';
    this.code = code;
    this.status = status;
    this.linkId = linkId;
  }
}

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

const toSignupError = (status: number, body: unknown): TravelBallSignupError => {
  const record = (body ?? {}) as { code?: unknown; message?: unknown; linkId?: unknown };
  const bodyCode = typeof record.code === 'string' ? record.code : null;
  const message = text(record.message);

  if (status === 429) {
    return new TravelBallSignupError('rate_limited', RATE_LIMITED_MESSAGE, status);
  }

  if (bodyCode && KNOWN_CODES.has(bodyCode)) {
    return new TravelBallSignupError(
      bodyCode as TravelBallSignupErrorCode,
      message ?? UNKNOWN_MESSAGE,
      status,
      text(record.linkId),
    );
  }

  if (status === 404 && !bodyCode) {
    // The RequireFeature filter returns a bare "Not found." when the flag is off. Clients fail
    // closed: treat it as the feature being disabled so the UI can hide the signup CTA. A coded 404
    // is about a row, never the feature, so it never lands here.
    return new TravelBallSignupError(
      'travel_ball_disabled',
      message ?? 'Travel ball is not yet available.',
      status,
    );
  }

  if (status === 401) {
    return new TravelBallSignupError(
      'unauthorized',
      message ?? 'Your session has expired. Sign in again to continue.',
      status,
    );
  }

  return new TravelBallSignupError('unknown', message ?? UNKNOWN_MESSAGE, status);
};

/**
 * Any failure from a travel ball call, as a `TravelBallSignupError` with a displayable message. Shared
 * with the family reads (`services/travelBallFamily.ts`) so every page branches on one vocabulary.
 */
export const fromApiError = (error: unknown): TravelBallSignupError => {
  if (error instanceof TravelBallSignupError) return error;
  if (error instanceof ApiError) {
    if (error.statusCode === 0) {
      return new TravelBallSignupError('network', NETWORK_MESSAGE, 0);
    }
    return toSignupError(error.statusCode, error.response);
  }
  return new TravelBallSignupError('unknown', UNKNOWN_MESSAGE, 0);
};

/**
 * Creates a player or parent/guardian account for the signed-in, verified Firebase account. Called
 * after the address is verified, idempotent on the Firebase account
 * (`status === 'existing'`), and refused with a `TravelBallSignupError` carrying the server's code.
 */
export const createTravelBallFamilySignup = async (
  request: CreateTravelBallFamilySignupRequest,
): Promise<TravelBallFamilySignupResponse> => {
  try {
    return await post<TravelBallFamilySignupResponse>('/api/public/travel-ball/signup/family', {
      familyRole: request.familyRole,
      firstName: request.firstName,
      lastName: request.lastName,
      acceptedTerms: request.acceptedTerms,
      acceptedPrivacy: request.acceptedPrivacy,
      platform: 'web',
    });
  } catch (error) {
    throw fromApiError(error);
  }
};

/**
 * The signed-in travel ball coach's (or program admin's) teams, scoped by the API to their program.
 */
export const listTravelBallTeams = async (): Promise<TravelBallTeam[]> => {
  try {
    return await get<TravelBallTeam[]>('/api/travel-ball/teams');
  } catch (error) {
    throw fromApiError(error);
  }
};

export interface TravelBallTeam {
  id: string;
  name: string;
  sport: string;
  playerLevel: string | null;
  travelBallOrganizationId: string;
  travelBallSeasonId: string | null;
  playerCount: number;
  createdAt: string;
  updatedAt: string;
}
